// Load disposable email domains with a fallback approach
let disposableEmailDomains = [];
let disposableDomainsLoaded = false;

const loadDisposableDomains = async () => {
  if (disposableDomainsLoaded) return disposableEmailDomains;
  
  try {
    // For modern JS environments, try to use the import assertion
    let domainsData;
    try {
      const response = await fetch('https://raw.githubusercontent.com/martenson/disposable-email-domains/master/disposable_email_blocklist.conf');
      const text = await response.text();
      domainsData = text.trim().split('\n').filter(domain => domain.trim() && !domain.startsWith('#'));
    } catch (fetchError) {
      // If fetch fails (e.g., in Node.js or offline), try require
      const fs = await import('fs');
      const path = await import('path');
      
      // Try to read from node_modules
      try {
        const packagePath = path.resolve('node_modules/disposable-email-domains/index.json');
        const fileContent = fs.readFileSync(packagePath, 'utf8');
        domainsData = JSON.parse(fileContent);
      } catch (fsError) {
        // Try alternative path structure
        const altPath = path.resolve('node_modules/disposable-email-domains/disposable_email_blocklist.conf');
        const fileContent = fs.readFileSync(altPath, 'utf8');
        domainsData = fileContent.trim().split('\n').filter(domain => domain.trim() && !domain.startsWith('#'));
      }
    }
    
    disposableEmailDomains = Array.isArray(domainsData) ? domainsData : [];
    console.log(`Loaded ${disposableEmailDomains.length} disposable email domains`);
  } catch (error) {
    console.warn('Could not load disposable-email-domains package, using fallback list:', error.message);
    // Comprehensive fallback list of common disposable and temporary email domains
    disposableEmailDomains = [
      // 10 minute mail services
      '10minutemail.com', '10minutemail.org', '10minutemail.net', '10minutemail.co.uk',
      '10minutemail.de', '10minutemail.pl',
      
      // Temp mail services
      'tempmail.org', 'temp-mail.org', 'tempmail.net', 'temp-mail.net', 'tempmail.co',
      'tempmail.email', 'tempmail.plus', 'tempmail.ninja',
      
      // Guerrilla mail
      'guerrillamail.com', 'guerrillamail.org', 'guerrillamail.net', 'guerrillamail.de',
      'guerrillamail.biz', 'guerrillamail.info', 'sharklasers.com', 'grr.la',
      'guerrillamailblock.com', 'pokemail.net', 'spam4.me',
      
      // Mailinator
      'mailinator.com', 'mailinator.net', 'mailinator.org', 'sogetthis.com',
      'spamhereat.com', 'spamherelots.com', 'spamherenow.com', 'spamhereplease.com',
      'thisisnotmyrealemail.com', 'emailmiser.com',
      
      // Other common disposable services
      'trashymail.com', 'trashmail.com', 'trashmail.org', 'trashmail.me',
      'trashmail.at', 'trash-mail.at', 'throwaway.email', 'getnada.com',
      'maildrop.cc', 'yopmail.com', 'yopmail.fr', 'yopmail.net',
      'mailnesia.com', 'mailcatch.com', 'mailexpire.com', 'mailtothis.com',
      'mailinator2.com', 'notmailinator.com', 'veryrealemail.com',
      'spamfree24.org', 'spamfree24.de', 'spamfree24.eu',
      'kurzepost.de', 'objectmail.com', 'proxymail.eu', 'rcpt.at',
      'wegwerfmail.de', 'wegwerfmail.net', 'wegwerfmail.org',
      'jetable.org', 'jetable.net', 'jetable.com',
      'mytemp.email', 'temp-email.org', 'email-temp.com'
    ];
  }
  
  disposableDomainsLoaded = true;
  return disposableEmailDomains;
};

// Common public email domains (the disposable-email-domains package focuses on disposable emails, 
// so we need to manually add the major public email providers)
const publicEmailDomains = new Set([
  // Major providers
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.it',
  'yahoo.es',
  'yahoo.ca',
  'yahoo.com.au',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.it',
  'hotmail.es',
  'outlook.com',
  'outlook.co.uk',
  'outlook.fr',
  'outlook.de',
  'outlook.it',
  'outlook.es',
  'live.com',
  'live.co.uk',
  'live.fr',
  'live.de',
  'live.it',
  'msn.com',
  'aol.com',
  'aol.co.uk',
  'aol.fr',
  'aol.de',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'proton.me',
  'tutanota.com',
  'zoho.com',
  'yandex.com',
  'yandex.ru',
  'mail.ru',
  'mail.com',
  'gmx.com',
  'gmx.de',
  'web.de',
  't-online.de',
  'orange.fr',
  'laposte.net',
  'free.fr',
  'wanadoo.fr',
  'comcast.net',
  'sbcglobal.net',
  'verizon.net',
  'att.net',
  'bellsouth.net',
  'charter.net',
  'cox.net',
  'earthlink.net',
  'optonline.net',
  'rocketmail.com',
  'aim.com',
  'rediffmail.com',
  'fastmail.com',
  'fastmail.fm',
  'hushmail.com',
  'guerrillamail.com',
  '10minutemail.com',
  'mailinator.com',
  'temp-mail.org',
  ...disposableEmailDomains
]);

/**
 * Checks if an email domain is a business/corporate domain
 * @param {string} domain - The domain to check (e.g., 'example.com')
 * @returns {Promise<boolean>} - True if it's likely a business domain, false if it's a public email provider
 */
export const isBusinessDomain = async (domain) => {
  if (!domain || typeof domain !== 'string') {
    return false;
  }
  
  const lowerDomain = domain.toLowerCase().trim();
  
  // Check if it's in our list of major public email providers
  if (publicEmailDomains.has(lowerDomain)) {
    return false;
  }
  
  // Load and check disposable email domains
  const disposableDomains = await loadDisposableDomains();
  if (disposableDomains.includes(lowerDomain)) {
    return false;
  }
  
  // Additional checks for common patterns that indicate public domains
  const publicPatterns = [
    /^[\d]+mail\.com$/, // Numbered mail services
    /^temp.*mail.*\./,  // Temporary mail services
    /^throw.*away/,     // Throwaway email services
    /^guerrilla/,       // Guerrilla mail variants
    /^mailinator/,      // Mailinator variants
    /^sharklasers/,     // Sharklasers (mailinator)
    /^maildrop/,        // Maildrop services
    /^.*\.tempmail/,    // Tempmail variants
    /^.*\.disposable/,  // Disposable variants
  ];
  
  for (const pattern of publicPatterns) {
    if (pattern.test(lowerDomain)) {
      return false;
    }
  }
  
  // If it passed all checks, consider it a business domain
  return true;
};

/**
 * Extracts domain from email address
 * @param {string} email - The email address
 * @returns {string|null} - The domain part or null if invalid
 */
export const extractDomain = (email) => {
  if (!email || typeof email !== 'string') {
    return null;
  }
  
  const parts = email.trim().split('@');
  if (parts.length !== 2) {
    return null;
  }
  
  return parts[1].toLowerCase();
};

/**
 * Checks if an email address is from a business domain
 * @param {string} email - The email address to check
 * @returns {Promise<boolean>} - True if it's from a business domain
 */
export const isBusinessEmail = async (email) => {
  const domain = extractDomain(email);
  return domain ? await isBusinessDomain(domain) : false;
}; 